import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, gql } from '@apollo/client'
import Button from '../../components/Button'
import { IonCard, IonCardContent, IonCardHeader, IonCardTitle } from '@ionic/react'
import { useUser } from '../../providers/Auth/hooks'
import Page from '../../components/Page'
import Header from '../../components/Header'
import Content from '../../components/Content'
import useToast from '../../providers/Toast/hooks'
import './style.scss'

const GET_ALL_TRAINERS = gql`
  query GetAllTrainers {
    getAllTrainers {
      sub
      given_name
      family_name
      name
      email
      roles
      isOriginalAdmin
    }
  }
`

const PROMOTE_TRAINER = gql`
  mutation PromoteTrainerToAdmin($email: String!) {
    promoteTrainerToAdmin(email: $email) {
      sub
      roles
      isOriginalAdmin
    }
  }
`

const DEMOTE_ADMIN = gql`
  mutation DemoteAdminToTrainer($email: String!) {
    demoteAdminToTrainer(email: $email) {
      sub
      roles
      isOriginalAdmin
    }
  }
`

const AdminManagement: React.FC = () => {
  const { t } = useTranslation()
  const user = useUser()
  const [searchTerm, setSearchTerm] = useState('')
  const [present] = useToast()

  const { data: trainersData, loading: trainersLoading, refetch: refetchTrainers } = useQuery(GET_ALL_TRAINERS)

  const [promoteTrainer, { loading: promoting }] = useMutation(PROMOTE_TRAINER)
  const [demoteAdmin, { loading: demoting }] = useMutation(DEMOTE_ADMIN)

  if (!user?.user.roles.includes('admin')) {
    return <div>Access denied</div>
  }

  const handlePromote = async (email: string) => {
    try {
      await promoteTrainer({ variables: { email } })
      await refetchTrainers()
      present(t('admin.management.label.trainerAddSuccess'), 'success')
    } catch (error) {
      console.error('Error promoting trainer:', error)
      present(`${t('admin.management.label.trainerAddError')}: ${error instanceof Error ? error.message : 'Unknown error'}`, 'danger')
    }
  }

  const handleDemote = async (email: string) => {
    try {
      await demoteAdmin({ variables: { email } })
      await refetchTrainers()
      present(t('admin.management.label.trainerDemoteSuccess'), 'success')
    } catch (error) {
      console.error('Error demoting admin:', error)
      present(`${t('admin.management.label.trainerDemoteError')}: ${error instanceof Error ? error.message : 'Unknown error'}`, 'danger')
    }
  }

  // Use getAllTrainers data from backend
  const trainers = trainersData?.getAllTrainers?.map((trainer: any) => ({
    ...trainer,
    name: trainer.name || `${trainer.given_name || ''} ${trainer.family_name || ''}`.trim()
  })) || []
  const filteredTrainers = trainers.filter((trainer: any) =>
    trainer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trainer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (trainersLoading) return <div>Loading...</div>

  return (
    <Page>
      <Header title={t('admin.mangement.appBar.title')} buttonLeft="back">
      </Header>
      <Content>
        <div className="admin-management">
      <div className="admin-management__header">
        <h1>{t('admin.mangement.title')}</h1>
        <p>{t('admin.mangement.description')}</p>

      </div>

      <div className="admin-management__search">
        <input
          type="text"
          placeholder={t('admin.mangement.search.placeholder') as string}
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value) }}
          className="admin-management__search-input"
        />
      </div>

      <div className="admin-management__list">
        {filteredTrainers.map((trainer: any) => {
          const isAdmin = trainer.roles.includes('admin')
          const isElevated = trainer.roles.includes('elevated-trainer')
          const isOriginalAdmin = trainer.isOriginalAdmin
          const canBeDemoted = isAdmin && !isOriginalAdmin

          return (
            <IonCard key={trainer.email} className="admin-management__card">
              <IonCardHeader>
                <IonCardTitle className="admin-management__trainer-name">
                  {trainer.name}
                </IonCardTitle>
                <p className="admin-management__trainer-email">{trainer.email}</p>
              </IonCardHeader>
              <IonCardContent>
                <div className="admin-management__roles">
                  {isOriginalAdmin && (
                    <span className="admin-management__badge admin-management__badge--original">
                      {t('admin.management.label.originalAdmin')}
                    </span>
                  )}
                  {isAdmin && !isOriginalAdmin && (
                    <span className="admin-management__badge admin-management__badge--admin">
                      {t('admin.management.label.admin')}
                    </span>
                  )}
                  {isElevated && (
                    <span className="admin-management__badge admin-management__badge--elevated">
                      {t('admin.management.label.elevatedTrainer')}
                    </span>
                  )}
                  {trainer.roles.includes('trainer') && !isAdmin && !isElevated && (
                    <span className="admin-management__badge admin-management__badge--trainer">
                      {t('admin.management.label.trainer')}
                    </span>
                  )}
                </div>

                <div className="admin-management__actions">
                  {!isAdmin && !isElevated && (
                    <Button
                      onClick={async () => { await handlePromote(trainer.email) }}
                      disabled={promoting}
                      buttonSize="small"
                      color="primary"
                    >
                      {promoting ? t('admin.management.label.promoting') : t('admin.management.label.promoted')}
                    </Button>
                  )}

                  {isElevated && !isAdmin && (
                    <Button
                      onClick={async () => { await handleDemote(trainer.email) }}
                      disabled={demoting}
                      buttonSize="small"
                      color="secondary"
                    >
                      {demoting ? t('admin.management.label.demoting') : t('admin.management.label.demoted')}
                    </Button>
                  )}

                  {canBeDemoted && (
                    <Button
                      onClick={async () => { await handleDemote(trainer.email) }}
                      disabled={demoting}
                      buttonSize="small"
                      color="danger"
                    >
                      {demoting ? t('admin.management.label.demoting') : t('admin.management.label.demoteAdmin')}
                    </Button>
                  )}

                  {isOriginalAdmin && (
                    <span className="admin-management__protected">
                      {t('admin.management.label.demoteProtection')}
                    </span>
                  )}
                </div>
              </IonCardContent>
            </IonCard>
          )
        })}
      </div>

      {filteredTrainers.length === 0 && (
        <div className="admin-management__empty">
          {t('admin.management.label.noTrainers')}
        </div>
      )}
        </div>
      </Content>
    </Page>
  )
}

export default AdminManagement
